/**
 * Adyen payment panels: card and BLIK.
 *
 * Markup contract, on the .crdPay_item of the payment method:
 *   <div class="crdPay_item" data-adyen-method="card"
 *        x-data="adyenMethod({ type: 'card', method: 'adyencreditcard', clientKey: '…',
 *                              environment: 'test', locale: 'de-DE', errorText: '…' })">
 *     <div x-ref="storedCard">…<button @click="editStoredCard()">…</button></div>
 *     <div x-ref="container"></div>
 *     <p class="errors_wrapper" x-show="error" x-text="error"></p>
 *
 * One AdyenCheckout instance carries the whole page; it needs to know every method
 * on it, which is why it is built from the data-adyen-method elements in the markup
 * and not from the order in which the components start.
 *
 * The panel does not own the submit: it registers a hook with the enclosing
 * validatedForm, which runs it after the form itself is valid. The hook lets a
 * method through that is not chosen, asks Adyen to tokenise otherwise, and keeps
 * the form open when Adyen refuses.
 */
import { postAction } from '../shell/request.js';

const PAYMENT_METHODS = {
    card: { name: 'Credit Card', type: 'scheme', brands: ['amex', 'mc', 'visa', 'cartebancaire'] },
    blik: { name: 'BLIK', type: 'blik' },
};

const CARD_CONFIG = {
    styles: {
        base: { fontSize: '14px' },
        placeholder: { color: '#707070', fontSize: '14px', fontWeight: 400 },
    },
    hasHolderName: true,
    holderNameRequired: true,
    showImage: true,
    disableIOSArrowKeys: false,
};

const endpoint = (status) => `/${URLSEGMENT}index.php?cl=payment&fnc=adyen_ajaxHandlePayment&status=${status}`;

let checkout = null;
// the panel that is waiting for Adyen to answer its submit
let pending = null;

/** Adyen reads a nested object, the shop's request parameters are flat names with brackets. */
function flatten(value, prefix, target = new URLSearchParams()) {
    if (value === null || typeof value !== 'object') {
        target.set(prefix, String(value));
        return target;
    }

    Object.entries(value).forEach(([key, entry]) => flatten(entry, `${prefix}[${key}]`, target));

    return target;
}

async function checkoutInstance({ clientKey, environment, locale }) {
    if (checkout) {
        return checkout;
    }

    const types = [...document.querySelectorAll('[data-adyen-method]')]
        .map((element) => PAYMENT_METHODS[element.dataset.adyenMethod]);

    checkout = await AdyenCheckout({
        paymentMethodsResponse: { paymentMethods: types },
        clientKey,
        locale,
        environment,
        showPayButton: false,
        onSubmit: async (state) => {
            pending?.decorate(state.data);
            try {
                await postAction(endpoint('SUCCESS'), flatten(state.data, 'data'));
                pending?.resolve();
            } catch (error) {
                pending?.reject(error);
            }
        },
        onError: async (error) => {
            await postAction(endpoint('ERROR'), { message: error.message ?? String(error) });
            pending?.reject(error);
        },
    });

    return checkout;
}

document.addEventListener('alpine:init', () => {
    Alpine.data('adyenMethod', ({ type, method, clientKey, environment, locale, errorText }) => {
        // an Adyen component behind Alpine's reactive proxy never receives its own state: it stays out of the data
        let component = null;

        return {
            hasStoredCard: false,
            endDigits: null,
            error: '',

            init() {
                this.hasStoredCard = Boolean(this.$refs.storedCard);
                this.addBeforeSubmit(() => this.confirm());

                // paymentId belongs to the enclosing paymentStep: mount as soon as this method is chosen
                Alpine.effect(() => {
                    if (this.paymentId === method) {
                        this.mount();
                    }
                });
            },

            async mount() {
                if (component || this.hasStoredCard) {
                    return;
                }

                try {
                    const instance = await checkoutInstance({ clientKey, environment, locale });

                    component = instance.create(type, this.componentConfig());
                    component.mount(this.$refs.container);
                } catch (error) {
                    this.fail(error);
                }
            },

            componentConfig() {
                if (type !== 'card') {
                    return {};
                }

                return {
                    ...CARD_CONFIG,
                    onFieldValid: (field) => {
                        if (field.encryptedFieldName === 'encryptedCardNumber') {
                            this.endDigits = field.endDigits;
                        }
                    },
                };
            },

            /** Drops the card the session still holds and asks for a new one. */
            editStoredCard() {
                document.getElementById('adyen_checkoutAttemptId')?.remove();
                this.hasStoredCard = false;
                this.mount();
            },

            /** Only dev and staging render the selector; the case steers the test acquirer's answer. */
            applyDebugCase(value) {
                document.cookie = `adyenDebugCase=${value}; path=/`;
            },

            async confirm() {
                if (this.paymentId !== method) {
                    return true;
                }
                if (this.hasStoredCard) {
                    return true;
                }
                if (!component) {
                    this.fail(new Error(`Adyen component ${type} is not mounted`));
                    return false;
                }
                if (!component.isValid) {
                    component.showValidation();
                    return false;
                }

                this.error = '';

                return new Promise((resolve) => {
                    pending = {
                        decorate: (data) => {
                            if (type === 'card') {
                                data.adyenCCEndDigits = this.endDigits;
                            }
                        },
                        resolve: () => resolve(true),
                        reject: (error) => {
                            this.fail(error);
                            resolve(false);
                        },
                    };

                    component.submit();
                });
            },

            fail(error) {
                this.error = errorText;
                if (environment !== 'live') {
                    console.error('[Adyen]', error);
                }
            },
        };
    });
});
